/**
 * <ry-slider>
 *
 * Range slider component with single or dual handles.
 *
 * Usage:
 * <ry-slider min="0" max="100" value="50"></ry-slider>
 * <ry-slider min="0" max="100" start="25" end="75" range labeled></ry-slider>
 */

import { RyElement } from '../core/ry-element.js';

export class RySlider extends RyElement {
  #dragging = false;
  #activeThumb: 'value' | 'start' | 'end' | null = null;
  #track: HTMLElement | null = null;
  #fill: HTMLElement | null = null;
  #thumbs: Map<string, HTMLElement> = new Map();
  #tooltips: Map<string, HTMLElement> = new Map();

  static observedAttributes = ['min', 'max', 'step', 'value', 'start', 'end', 'disabled'] as const;

  setup(): void {
    this.#buildMarkup();
    this.#bindEvents();
    this.#updatePositions();
  }

  #buildMarkup(): void {
    const isRange = this.hasAttribute('range');
    const showLabels = this.hasAttribute('labeled');
    const showTooltip = this.hasAttribute('tooltip');
    const isVertical = this.hasAttribute('vertical');

    let html = `
      <div data-ry-target="track" class="ry-slider__track">
        <div data-ry-target="fill" class="ry-slider__fill"></div>
    `;

    if (isRange) {
      html += `
        <div data-ry-target="thumb-start" class="ry-slider__thumb" tabindex="0" role="slider" aria-label="Start value">
          ${showTooltip ? '<span data-ry-target="tooltip-start" class="ry-slider__tooltip"></span>' : ''}
        </div>
        <div data-ry-target="thumb-end" class="ry-slider__thumb" tabindex="0" role="slider" aria-label="End value">
          ${showTooltip ? '<span data-ry-target="tooltip-end" class="ry-slider__tooltip"></span>' : ''}
        </div>
      `;
    } else {
      html += `
        <div data-ry-target="thumb-value" class="ry-slider__thumb" tabindex="0" role="slider" aria-label="Value">
          ${showTooltip ? '<span data-ry-target="tooltip-value" class="ry-slider__tooltip"></span>' : ''}
        </div>
      `;
    }

    html += '</div>';

    if (showLabels) {
      html += this.#buildLabels();
    }

    this.innerHTML = html;

    // Cache references
    this.#track = this.$('[data-ry-target="track"]');
    this.#fill = this.$('[data-ry-target="fill"]');

    if (isRange) {
      this.#thumbs.set('start', this.$('[data-ry-target="thumb-start"]')!);
      this.#thumbs.set('end', this.$('[data-ry-target="thumb-end"]')!);
      if (showTooltip) {
        this.#tooltips.set('start', this.$('[data-ry-target="tooltip-start"]')!);
        this.#tooltips.set('end', this.$('[data-ry-target="tooltip-end"]')!);
      }
    } else {
      this.#thumbs.set('value', this.$('[data-ry-target="thumb-value"]')!);
      if (showTooltip) {
        this.#tooltips.set('value', this.$('[data-ry-target="tooltip-value"]')!);
      }
    }
  }

  #buildLabels(): string {
    const min = this.min;
    const max = this.max;
    const customLabels = this.getAttribute('labels')?.split(',');
    const labelType = this.getAttribute('label-type') || 'number';

    let labels: string[] = [];

    if (customLabels) {
      labels = customLabels;
    } else {
      // Show 5 evenly distributed labels (always include min and max)
      const labelCount = 5;
      for (let i = 0; i < labelCount; i++) {
        const ratio = i / (labelCount - 1);
        const val = min + ratio * (max - min);
        if (labelType === 'letter') {
          labels.push(String.fromCharCode(65 + i));
        } else {
          // Round to reasonable precision
          const rounded = Math.round(val * 100) / 100;
          labels.push(String(Number.isInteger(rounded) ? rounded : rounded.toFixed(1)));
        }
      }
    }

    return `
      <div data-ry-target="labels" class="ry-slider__labels">
        ${labels.map(l => `<span>${l}</span>`).join('')}
      </div>
    `;
  }

  #bindEvents(): void {
    // Track click
    if (this.#track) {
      this.on(this.#track, 'pointerdown', this.#handleTrackDown);
    }

    // Thumb events
    for (const [key, thumb] of this.#thumbs) {
      this.on(thumb, 'pointerdown', (e) => this.#handleThumbDown(e, key));
      this.on(thumb, 'keydown', (e) => this.#handleKeydown(e, key));
    }

    // Document events for drag
    this.on(document, 'pointermove', this.#handlePointerMove);
    this.on(document, 'pointerup', this.#handlePointerUp);
  }

  #handleTrackDown = (e: PointerEvent): void => {
    if (this.disabled) return;

    const value = this.#valueFromPointer(e);
    const isRange = this.hasAttribute('range');

    if (isRange) {
      // Move closest thumb
      const distToStart = Math.abs(value - this.start);
      const distToEnd = Math.abs(value - this.end);
      if (distToStart < distToEnd) {
        this.start = value;
        this.#activeThumb = 'start';
      } else {
        this.end = value;
        this.#activeThumb = 'end';
      }
    } else {
      this.value = value;
      this.#activeThumb = 'value';
    }

    this.#dragging = true;
    this.#updatePositions();
    this.#emitInput();
  };

  #handleThumbDown = (e: PointerEvent, key: string): void => {
    if (this.disabled) return;
    e.stopPropagation();
    this.#dragging = true;
    this.#activeThumb = key as 'value' | 'start' | 'end';
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  };

  #handlePointerMove = (e: PointerEvent): void => {
    if (!this.#dragging || !this.#activeThumb) return;

    const value = this.#valueFromPointer(e);
    const isRange = this.hasAttribute('range');

    if (isRange) {
      if (this.#activeThumb === 'start') {
        // Prevent crossover
        this.start = Math.min(value, this.end);
      } else if (this.#activeThumb === 'end') {
        this.end = Math.max(value, this.start);
      }
    } else {
      this.value = value;
    }

    this.#updatePositions();
    this.#emitInput();
  };

  #handlePointerUp = (): void => {
    if (this.#dragging) {
      this.#dragging = false;
      this.#activeThumb = null;
      this.#emitChange();
    }
  };

  #handleKeydown = (e: KeyboardEvent, key: string): void => {
    if (this.disabled) return;

    const step = this.step || 1;
    const largeStep = (this.max - this.min) / 10;
    let delta = 0;

    switch (e.key) {
      case 'ArrowRight':
      case 'ArrowUp':
        delta = this.hasAttribute('reversed') ? -step : step;
        break;
      case 'ArrowLeft':
      case 'ArrowDown':
        delta = this.hasAttribute('reversed') ? step : -step;
        break;
      case 'PageUp':
        delta = largeStep;
        break;
      case 'PageDown':
        delta = -largeStep;
        break;
      case 'Home':
        delta = this.min - this.#getValueForKey(key);
        break;
      case 'End':
        delta = this.max - this.#getValueForKey(key);
        break;
      default:
        return;
    }

    e.preventDefault();
    const newValue = this.#clamp(this.#getValueForKey(key) + delta);

    if (key === 'start') {
      this.start = Math.min(newValue, this.end);
    } else if (key === 'end') {
      this.end = Math.max(newValue, this.start);
    } else {
      this.value = newValue;
    }

    this.#updatePositions();
    this.#emitInput();
    this.#emitChange();
  };

  #getValueForKey(key: string): number {
    if (key === 'start') return this.start;
    if (key === 'end') return this.end;
    return this.value;
  }

  #valueFromPointer(e: PointerEvent): number {
    if (!this.#track) return this.min;

    const rect = this.#track.getBoundingClientRect();
    const isVertical = this.hasAttribute('vertical');
    const isReversed = this.hasAttribute('reversed');

    let ratio: number;
    if (isVertical) {
      ratio = (rect.bottom - e.clientY) / rect.height;
    } else {
      ratio = (e.clientX - rect.left) / rect.width;
    }

    if (isReversed) ratio = 1 - ratio;
    ratio = Math.max(0, Math.min(1, ratio));

    const rawValue = this.min + ratio * (this.max - this.min);
    return this.#snapToStep(rawValue);
  }

  #snapToStep(value: number): number {
    const step = this.step;
    if (step === 0) return this.#clamp(value);
    const snapped = Math.round((value - this.min) / step) * step + this.min;
    return this.#clamp(snapped);
  }

  #clamp(value: number): number {
    return Math.max(this.min, Math.min(this.max, value));
  }

  #updatePositions(): void {
    const isRange = this.hasAttribute('range');
    const isVertical = this.hasAttribute('vertical');
    const isReversed = this.hasAttribute('reversed');

    const toPercent = (val: number) => {
      let pct = ((val - this.min) / (this.max - this.min)) * 100;
      if (isReversed) pct = 100 - pct;
      return pct;
    };

    if (isRange) {
      const startPct = toPercent(this.start);
      const endPct = toPercent(this.end);
      const lowPct = Math.min(startPct, endPct);
      const highPct = Math.max(startPct, endPct);

      if (this.#fill) {
        if (isVertical) {
          this.#fill.style.bottom = `${lowPct}%`;
          this.#fill.style.height = `${highPct - lowPct}%`;
          this.#fill.style.left = '';
          this.#fill.style.width = '';
        } else {
          this.#fill.style.left = `${lowPct}%`;
          this.#fill.style.width = `${highPct - lowPct}%`;
          this.#fill.style.bottom = '';
          this.#fill.style.height = '';
        }
      }

      const startThumb = this.#thumbs.get('start');
      const endThumb = this.#thumbs.get('end');
      if (startThumb) {
        if (isVertical) {
          startThumb.style.bottom = `${startPct}%`;
          startThumb.style.left = '';
        } else {
          startThumb.style.left = `${startPct}%`;
          startThumb.style.bottom = '';
        }
      }
      if (endThumb) {
        if (isVertical) {
          endThumb.style.bottom = `${endPct}%`;
          endThumb.style.left = '';
        } else {
          endThumb.style.left = `${endPct}%`;
          endThumb.style.bottom = '';
        }
      }

      // Update tooltips
      this.#updateTooltip('start', this.start);
      this.#updateTooltip('end', this.end);

      // Update ARIA
      startThumb?.setAttribute('aria-valuenow', String(this.start));
      startThumb?.setAttribute('aria-valuemin', String(this.min));
      startThumb?.setAttribute('aria-valuemax', String(this.end));
      endThumb?.setAttribute('aria-valuenow', String(this.end));
      endThumb?.setAttribute('aria-valuemin', String(this.start));
      endThumb?.setAttribute('aria-valuemax', String(this.max));
    } else {
      const pct = toPercent(this.value);

      if (this.#fill) {
        if (isVertical) {
          this.#fill.style.bottom = '0';
          this.#fill.style.height = `${pct}%`;
          this.#fill.style.left = '';
          this.#fill.style.width = '';
        } else {
          this.#fill.style.left = '0';
          this.#fill.style.width = `${pct}%`;
          this.#fill.style.bottom = '';
          this.#fill.style.height = '';
        }
      }

      const thumb = this.#thumbs.get('value');
      if (thumb) {
        if (isVertical) {
          thumb.style.bottom = `${pct}%`;
          thumb.style.left = '';
        } else {
          thumb.style.left = `${pct}%`;
          thumb.style.bottom = '';
        }
      }

      // Update tooltip
      this.#updateTooltip('value', this.value);

      // Update ARIA
      thumb?.setAttribute('aria-valuenow', String(this.value));
      thumb?.setAttribute('aria-valuemin', String(this.min));
      thumb?.setAttribute('aria-valuemax', String(this.max));
    }
  }

  #updateTooltip(key: string, value: number): void {
    const tooltip = this.#tooltips.get(key);
    if (tooltip) {
      const decimals = this.step === 0 ? 2 : (String(this.step).split('.')[1]?.length || 0);
      tooltip.textContent = value.toFixed(decimals);
    }
  }

  #emitInput(): void {
    if (this.hasAttribute('range')) {
      this.emit('input', { start: this.start, end: this.end });
    } else {
      this.emit('input', { value: this.value });
    }
  }

  #emitChange(): void {
    if (this.hasAttribute('range')) {
      this.emit('change', { start: this.start, end: this.end });
    } else {
      this.emit('change', { value: this.value });
    }
  }

  // --- Public API ---

  get min(): number {
    return parseFloat(this.getAttribute('min') ?? '0');
  }
  set min(val: number) {
    this.setAttribute('min', String(val));
  }

  get max(): number {
    return parseFloat(this.getAttribute('max') ?? '100');
  }
  set max(val: number) {
    this.setAttribute('max', String(val));
  }

  get step(): number {
    return parseFloat(this.getAttribute('step') ?? '1');
  }
  set step(val: number) {
    this.setAttribute('step', String(val));
  }

  get value(): number {
    return parseFloat(this.getAttribute('value') ?? String(this.min));
  }
  set value(val: number) {
    this.setAttribute('value', String(this.#clamp(val)));
  }

  get start(): number {
    return parseFloat(this.getAttribute('start') ?? String(this.min));
  }
  set start(val: number) {
    this.setAttribute('start', String(this.#clamp(val)));
  }

  get end(): number {
    return parseFloat(this.getAttribute('end') ?? String(this.max));
  }
  set end(val: number) {
    this.setAttribute('end', String(this.#clamp(val)));
  }

  get disabled(): boolean {
    return this.hasAttribute('disabled');
  }
  set disabled(val: boolean) {
    if (val) {
      this.setAttribute('disabled', '');
    } else {
      this.removeAttribute('disabled');
    }
  }
}

customElements.define('ry-slider', RySlider);
